import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { SalesOrderClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectSalesOrderState } from "../sales-order.selectors";
import { SalesOrdersActions } from "./sales-orders.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// SALES ORDERS LIST EFFECTS - Single effect, no chained actions
// ============================================================================

@Injectable()
export class SalesOrdersEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly salesOrderClient = inject(SalesOrderClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesOrdersActions.load),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectSalesOrderState),
            ),
            switchMap(([action, selectedCompany, state]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(SalesOrdersActions.loadFailure({
                        error: { message: "No company selected" },
                        toastKey: "SALES_ORDER.TOAST.LIST_ERROR",
                    }));
                }

                // Merge: action params → current state → defaults
                const params = {
                    pagination: {
                        pageNumber: action.pagination?.pageNumber ?? state?.pageNumber ?? 1,
                        pageSize: action.pagination?.pageSize ?? state?.pageSize ?? 20,
                    },
                    sorting: {
                        sortProperty: action.sorting?.sortProperty ?? state?.sortProperty ?? "",
                        sortAscending: action.sorting?.sortAscending ?? state?.sortAscending ?? true,
                    },
                    orderNo: action.orderNo ?? state?.orderNo ?? "",
                    externalDocumentNo: action.externalDocumentNo ?? state?.externalDocumentNo ?? "",
                    salesChannelCode: action.salesChannelCode ?? state?.salesChannelCode ?? "",
                    customerContactName: action.customerContactName ?? state?.customerContactName ?? "",
                    customerNo: action.customerNo ?? state?.customerNo ?? "",
                    salespersonCode: action.salespersonCode ?? state?.salespersonCode ?? "",
                };

                return this.salesOrderClient
                    .getBcSalesOrders(
                        companyId,
                        params.orderNo || undefined,
                        params.externalDocumentNo || undefined,
                        params.salesChannelCode || undefined,
                        params.customerContactName || undefined,
                        params.customerNo || undefined,
                        params.salespersonCode || undefined,
                        params.pagination.pageNumber,
                        params.pagination.pageSize,
                        params.sorting.sortProperty || undefined,
                        params.sorting.sortAscending,
                    )
                    .pipe(
                        map(result => SalesOrdersActions.loadSuccess({
                            result,
                            params,
                            toastKey: "SALES_ORDER.TOAST.LIST_SUCCESS",
                        })),
                        catchError(error => of(SalesOrdersActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "SALES_ORDER.TOAST.LIST_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
